import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { inject, observer } from 'mobx-react';
import { Icon, Modal, Tooltip, message } from '@/components';
import { RootStore, members, transcribe } from '@/models';
import ActionBtn from '../ActionBtn';
import styles from './index.less';
import classNames from 'classnames';
import { elkLog } from '@/utils/elkReport';
import { ShareScreenFull } from '@/models/meeting';

interface Props extends Partial<Pick<RootStore, 'meeting'>> {
  meeting?: RootStore['meeting'];
  chat?: RootStore['chat'];
  user?: RootStore['user'];
}

export enum TranscribeStatus {
  none = 0, // 未开启转录
  notTranscribe = 1, // 未开启转录,申请转录
  intranscribe = 2, // 在转录中
  pauseTranscribe = 3, //转录暂停
}

const TranscribeBtn: React.FC<Props> = inject(({ store }) => {
  return {
    record: store.record,
    chat: store.chat,
    meeting: store.meeting,
    user: store.user,
  };
})(
  observer((props) => {
    const { chat, meeting, user } = props;
    const { formatMessage } = useIntl();

    // 申请转录功能
    const applyTranscribe = () => {
      API.api.webSubtitleV3.requestStartTranscription
        .request(
          { meetingId: meeting?.info?.meetingId },
          {
            channelId: meeting?.info?.channelId,
          }
        )
        .then((res) => {
          if (res.status === 1) {
            message.info({
              content: formatMessage({ id: 'REQUEST_SENT' }),
              icon: null,
            });
          }
          elkLog('申请转录成功');
        });
    };
    // 主持人直接开启转录功能
    const startTranscription = () => {
      API.client.meetingSubtitleV3.startTranscription
        .request(
          { meetingId: meeting?.info?.meetingId },
          {
            channelId: meeting?.info?.channelId,
          },
          {
            headers: {
              deviceId: meeting?.info?.host?.deviceId,
            },
          }
        )
        .then((res) => {
          if (res.status === 1) {
            transcribe.setTranscribeStatus(TranscribeStatus.intranscribe);
            transcribe.setTranscribeVisible(!transcribe.transcribeVisible);
            chat?.chatVisible && chat?.setChatVisible(!chat?.chatVisible);
            meeting?.peoplePanelShow && meeting?.setPeoplePanelShow(!meeting?.peoplePanelShow);
            meeting?.shareScreenFull === ShareScreenFull.FULL &&
              meeting?.setShareScreenFull(ShareScreenFull.EXIT_FULL); //共享全屏状态下打开转录面板自动退出全屏

            message.info({
              content: formatMessage({ id: 'TRANSCRIPTION_IS_ENABLED1' }),
              icon: null,
            });
            elkLog('直接开启转录成功');
          } else {
            message.error({
              content: formatMessage({ id: 'TRANSCRIPTION_ENABLE_FAILED' }),
              icon: null,
            });
          }
        });
    };

    // 打开转录按钮功能
    const openTranscribeBtn = () => {
      elkLog('点击转录按钮');
      console.log('meeting?: ', meeting);
      console.log('是否是主持人：', user, user?.uid, meeting?.info?.host?.uid);

      if (!meeting?.hostUid) {
        message.info({
          content: formatMessage({ id: 'canNotOpenTranscribe' }), // 暂时无法开启转录
          noIcon: true,
        });
        return;
      }
      API.api.webSubtitleV3.getTranscriptionStatus
        .request({
          channelId: meeting?.info?.channelId!,
          meetingId: meeting?.info?.meetingId!,
        })
        .then((res) => {
          if (res.status === 1 && res.data?.transcriptionStatus) {
            const status = res.data.transcriptionStatus;
            transcribe.setTranscribeStatus(status);
            if (
              status === TranscribeStatus.intranscribe ||
              status === TranscribeStatus.pauseTranscribe
            ) {
              transcribe.setTranscribeVisible(!transcribe.transcribeVisible);
              chat?.chatVisible && chat?.setChatVisible(!chat?.chatVisible);
              meeting?.peoplePanelShow && meeting?.setPeoplePanelShow(!meeting?.peoplePanelShow);
              meeting?.shareScreenFull === ShareScreenFull.FULL &&
                meeting?.setShareScreenFull(ShareScreenFull.EXIT_FULL); //共享全屏状态下打开转录面板自动退出全屏
              return;
            }
            if (status === TranscribeStatus.notTranscribe) {
              // 主持人直接开启转录，不需要申请
              if (user?.uid === meeting?.info?.host?.uid) {
                startTranscription();

                elkLog('主持人直接申请转录');
                return;
              }

              Modal.confirm({
                icon: ' ',
                content: formatMessage({ id: 'requestTranscriptionToHost' }), // 向主持人申请转录？
                okText: formatMessage({ id: 'CONFIRM' }),
                cancelText: formatMessage({
                  id: 'CANCEL',
                }),
                okButtonProps: { type: 'primary' },
                cancelButtonProps: { type: 'default' },
                onOk: () => {
                  elkLog('向主持人申请转录-确定');
                  applyTranscribe();
                },
              });
            }
          }
        });
    };

    const tooltip = useMemo(() => {
      if (!meeting?.hostUid) return formatMessage({ id: 'canNotOpenTranscribe' });
      return formatMessage({ id: 'transcriptionHelpTip' });
    }, [transcribe.transcribeVisible, meeting?.hostUid]);

    // 如果会议不支持转录功能，返回null
    if (!meeting?.ableAppsMap.Transcription) {
      return null;
    }

    // 转录按钮
    return (
      <Tooltip title={tooltip} className={classNames(styles.recordingBtn, {})}>
        <div
          className={classNames(styles.transcribe, {
            [styles.disable]: !meeting?.hostUid,
          })}
          onClick={openTranscribeBtn}
        >
          <Icon className={styles.icon} name="icon-summary" size={24} mode="symbol" />
          <span className={styles.title}>{formatMessage({ id: 'transcribe' })}</span>
        </div>
      </Tooltip>
    );
  })
);

export default TranscribeBtn;
